"use client";

import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChildProfileDialog } from "@/components/child-profile-dialog";
import type { Child } from "@/lib/types";

interface ChildSelectionProps {
  children: Child[];
  selectedChildId: string;
  onSelectChild: (childId: string) => void;
  onProfileSave: (data: Omit<Child, 'id' | 'userId'> | Omit<Child, 'userId'>) => void;
}

export function ChildSelection({
  children,
  selectedChildId,
  onSelectChild,
  onProfileSave,
}: ChildSelectionProps) {
  if (children.length === 0) {
    return (
      <div className="flex items-center justify-center p-6">
        <ChildProfileDialog onSave={onProfileSave} child={null}>
          <Button className="gap-2">
            <UserPlus className="h-4 w-4" />
            Add Child
          </Button>
        </ChildProfileDialog>
      </div>
    );
  }

  // If there's only one child, don't show tabs
  if (children.length === 1) {
    return null;
  }

  const handleTabChange = (value: string) => {
    onSelectChild(value);
  };

  return (
    <div className="bg-card px-4 pb-2 pt-0">
      <Tabs value={selectedChildId} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full gap-0 p-0 h-auto bg-transparent" style={{ gridTemplateColumns: `repeat(${children.length}, minmax(0, 1fr))` }}>
          {children.map((child) => (
            <TabsTrigger
              key={child.id}
              value={child.id}
              className="px-2 py-1.5 text-sm min-w-0 truncate rounded-none first:rounded-l-md last:rounded-r-md bg-muted hover:bg-muted/80 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-md data-[state=active]:border-b-2 data-[state=active]:border-primary transition-all"
            >
              {child.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}
